import React from "react";
import { cn } from "../../utils/cn";
import { Typography } from "../../primitives/typography";

export interface ResponsiveSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Section title displayed in the header
   */
  title?: string;
  /**
   * Optional icon component for the header
   */
  icon?: React.ComponentType<{ className?: string }>;
  /**
   * Optional description text
   */
  description?: string;
  /**
   * Optional badge text/type
   */
  badge?: {
    label: string;
    variant: "required" | "optional" | "default";
  };
  /**
   * Whether to remove border/shadow/radius on mobile
   * @default true
   */
  flatOnMobile?: boolean;
  /**
   * Whether to show a divider line at the bottom on mobile
   * @default false
   */
  hasMobileDivider?: boolean;
  /**
   * Optional action element (e.g. Edit button) in the header
   */
  action?: React.ReactNode;
}

const badgeStyles = {
  required: "bg-red-50 text-red-600 border-red-100",
  optional: "bg-primary/10 text-primary border-primary/20",
  default: "bg-gray-100 text-gray-600 border-gray-200",
};

/**
 * ResponsiveSection
 *
 * A container component that renders as a flat section on mobile
 * and a glassmorphism card on desktop.
 */
export const ResponsiveSection = React.forwardRef<
  HTMLDivElement,
  ResponsiveSectionProps
>(
  (
    {
      title,
      icon: Icon,
      description,
      badge,
      action,
      flatOnMobile = true,
      hasMobileDivider = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <>
        <div
          ref={ref}
          className={cn(
            // Base styles
            "transition-all duration-300",

            // Mobile styles (Flat)
            flatOnMobile
              ? "bg-white p-5 sm:p-8 border-none shadow-none rounded-none"
              : "bg-white p-6 rounded-2xl border border-gray-100 shadow-sm",

            // Desktop styles (Card)
            "md:bg-white/80 md:backdrop-blur-xl md:border md:border-gray-100",
            "md:rounded-[24px] md:shadow-sm",
            "md:p-10",
            "md:hover:shadow-md md:hover:border-primary/20",

            className,
          )}
          {...props}
        >
          {/* Header Section */}
          {(title ?? Icon ?? badge ?? action) && (
            <div className="flex items-center gap-3 mb-6">
              {Icon && (
                <div className="w-10 h-10 rounded-[12px] bg-primary/5 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
              )}

              {title && (
                <div className="flex-1">
                  <Typography
                    variant="h4"
                    className="text-lg sm:text-xl font-semibold text-gray-900 leading-snug tracking-tight"
                  >
                    {title}
                  </Typography>
                  {description && (
                    <Typography
                      variant="body-sm"
                      className="text-gray-500 font-medium mt-0.5 tracking-tight"
                    >
                      {description}
                    </Typography>
                  )}
                </div>
              )}

              {badge && (
                <span
                  className={cn(
                    "px-3 py-1 rounded-full text-2xs sm:text-xs font-semibold border flex-shrink-0 self-start mt-1",
                    badgeStyles[badge.variant],
                  )}
                >
                  {badge.label}
                </span>
              )}

              {action && (
                <div className="flex-shrink-0 self-start mt-0.5">{action}</div>
              )}
            </div>
          )}

          {/* Content */}
          {children}
        </div>

        {/* Mobile Dividers */}
        {hasMobileDivider && <div className="h-px bg-gray-50 md:hidden mx-5" />}
      </>
    );
  },
);

ResponsiveSection.displayName = "ResponsiveSection";
